"use client";

// Customer Story — mobile scroll-nav (Figma 5616:19421). A sticky horizontal
// bar of chips that mirror the article sections; the active chip carries the
// grey-100 "Scroll Indicator" gauge. Mobile counterpart of the desktop sidebar
// nav, and it fills with reading progress the same way — the Figma's 48px is
// the resting width a static frame can show, so it stays as the floor.
//
// Two layouts, because the consumers differ in kind:
//
// - `fit` (default) — a handful of short curated labels ("Challenge",
//   "Results"). They divide the row equally, as the Figma frame shows.
// - `scroll` — one chip per `##` heading of an article, so the count is the
//   author's, not the designer's, and the labels are full sentences. Chips keep
//   a legible floor width and the bar scrolls sideways, following the reader.

import { useEffect, useRef } from "react";

import { cn } from "@/lib/utils";

import { useActiveSection, useSectionProgress } from "./use-active-section";
import type { StoryNavItem } from "./use-active-section";

export type StoryScrollBarLayout = "fit" | "scroll";

// Scroll-layout chips size to their label between these bounds, so a heading
// spends the bar's horizontal space (which scrolls) before its vertical space
// (which is sticky over the article). Pinned to the floor alone, a long heading
// stacked five lines deep and doubled the bar's height.
const CHIP_WIDTH = "w-auto min-w-[148px] max-w-[260px]";

export function CustomerStoryScrollBar({
  items,
  ariaLabel,
  className,
  layout = "fit",
}: {
  items: StoryNavItem[];
  ariaLabel?: string;
  className?: string;
  layout?: StoryScrollBarLayout;
}) {
  const activeId = useActiveSection(items);
  const progress = useSectionProgress(activeId);
  const navRef = useRef<HTMLElement>(null);
  const activeRef = useRef<HTMLAnchorElement>(null);

  // Follow the reading position. Scrolls the bar only — `scrollIntoView` would
  // also scroll the page vertically and fight the reader.
  useEffect(() => {
    const nav = navRef.current;
    const chip = activeRef.current;
    if (!nav || !chip) return;
    if (nav.scrollWidth <= nav.clientWidth) return;

    const target = chip.offsetLeft - (nav.clientWidth - chip.offsetWidth) / 2;
    const left = Math.max(
      0,
      Math.min(target, nav.scrollWidth - nav.clientWidth)
    );

    nav.scrollTo({
      left,
      behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
        ? "auto"
        : "smooth",
    });
  }, [activeId]);

  return (
    <nav
      ref={navRef}
      aria-label={ariaLabel}
      className={cn(
        "bg-grey-0 border-grey-100 sticky top-[var(--nav-offset)] z-30 flex min-h-[72px] items-stretch gap-2 border px-6 py-3",
        layout === "scroll" &&
          "snap-x snap-proximity overflow-x-auto overscroll-x-contain [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
        className
      )}
    >
      {items.map((item) => {
        const active = item.id === activeId;
        return (
          <a
            key={item.id}
            ref={active ? activeRef : undefined}
            href={`#${item.id}`}
            aria-current={active ? "true" : undefined}
            className={cn(
              "bg-grey-50 relative flex min-h-[33px] items-center overflow-hidden rounded-lg py-1 pr-2 pl-2",
              layout === "scroll"
                ? cn("shrink-0 snap-start", CHIP_WIDTH)
                : "min-w-0 flex-1"
            )}
          >
            {active ? (
              <span
                aria-hidden
                className="bg-grey-100 absolute inset-y-0 left-0"
                style={{ width: `${progress * 100}%`, minWidth: "48px" }}
              />
            ) : null}
            <span className="text-grey-950 relative z-10 min-w-0 text-[12px] leading-[1.4] break-words">
              {item.label}
            </span>
          </a>
        );
      })}
    </nav>
  );
}
